import { IWorkingHours } from "@/api/businessLinesApi";
import React, { useState } from "react";

type Props = {
  workingHours: IWorkingHours;
  setWorkingHours: React.Dispatch<React.SetStateAction<IWorkingHours>>;
  isReadOnly?: boolean;
};

// Helper: minutes since midnight <-> "HH:MM"
function minutesToTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}
function timeStringToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

const DAY_LABELS: Record<string, string> = {
  mo: "Monday",
  tu: "Tuesday",
  we: "Wednesday",
  th: "Thursday",
  fr: "Friday",
  sa: "Saturday",
  su: "Sunday",
};

const DEFAULT_START = 8 * 60; // 08:00
const DEFAULT_END = 17 * 60; // 17:00

export const WorkingHoursTable: React.FC<Props> = ({
  workingHours,
  setWorkingHours,
  isReadOnly = false,
}) => {
  // Dropdown mode: 'weekdays', '247', 'custom'
  const [mode, setMode] = useState<"weekdays" | "247" | "custom">("custom");

  // Handle dropdown change
  const handleModeChange = (value: "weekdays" | "247" | "custom") => {
    setMode(value);
    if (value === "weekdays") {
      setWorkingHours({
        mo: {
          start: DEFAULT_START,
          end: DEFAULT_END,
          shifts: [{ start: DEFAULT_START, end: DEFAULT_END }],
        },
        tu: {
          start: DEFAULT_START,
          end: DEFAULT_END,
          shifts: [{ start: DEFAULT_START, end: DEFAULT_END }],
        },
        we: {
          start: DEFAULT_START,
          end: DEFAULT_END,
          shifts: [{ start: DEFAULT_START, end: DEFAULT_END }],
        },
        th: {
          start: DEFAULT_START,
          end: DEFAULT_END,
          shifts: [{ start: DEFAULT_START, end: DEFAULT_END }],
        },
        fr: {
          start: DEFAULT_START,
          end: DEFAULT_END,
          shifts: [{ start: DEFAULT_START, end: DEFAULT_END }],
        },
        sa: null,
        su: null,
      });
    } else if (value === "247") {
      setWorkingHours({
        mo: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        tu: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        we: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        th: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        fr: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        sa: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
        su: { start: 0, end: 1439, shifts: [{ start: 0, end: 1439 }] },
      });
    }
    // If 'custom', do not change workingHours
  };

  const handleDayToggle = (day: string, checked: boolean) => {
    if (mode !== "custom") return;
    console.log("Toggling day:", day, "checked:", checked);
    setWorkingHours((prev) => {
      const newState = {
        ...prev,
        [day]: checked
          ? {
              start: DEFAULT_START,
              end: DEFAULT_END,
              shifts: [
                {
                  start: DEFAULT_START,
                  end: DEFAULT_END,
                },
              ],
            }
          : null,
      };
      console.log("New working hours state:", newState);
      return newState;
    });
  };

  const handleTimeChange = (
    day: string,
    type: "start" | "end",
    value: string
  ) => {
    if (mode !== "custom") return;
    console.log("Changing time:", day, type, value);
    const minutes = timeStringToMinutes(value);
    setWorkingHours((prev) => {
      const schedule = prev[day as keyof IWorkingHours];
      if (!schedule) {
        console.log("No schedule found for day:", day);
        return prev;
      }
      const newSchedule = {
        ...schedule,
        [type]: minutes,
        shifts: [
          {
            ...schedule.shifts[0],
            [type]: minutes,
          },
        ],
      };
      const newState = {
        ...prev,
        [day]: newSchedule,
      };
      console.log("New working hours state after time change:", newState);
      return newState;
    });
  };

  // Apply to all functionality
  const handleApplyToAll = () => {
    if (mode !== "custom") return;

    // Get Monday's schedule as template (or first enabled day if Monday is not selected)
    const mondaySchedule = workingHours.mo;
    const templateSchedule =
      mondaySchedule ||
      Object.entries(workingHours).find(
        ([_, schedule]) => schedule !== null
      )?.[1];

    if (!templateSchedule) return;

    // Apply template only to currently selected days
    setWorkingHours((prev) => {
      const newWorkingHours = { ...prev };
      Object.keys(prev).forEach((day) => {
        // Only apply to days that are currently selected (not null)
        if (prev[day as keyof IWorkingHours] !== null) {
          newWorkingHours[day as keyof IWorkingHours] = templateSchedule;
        }
      });
      return newWorkingHours;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-medium text-zinc-700">Business Hours</h2>
          <div className="text-gray-500 text-xs">
            Required to configure all the time based on operations on your
            Number.
          </div>
        </div>
        <div>
          <div className="relative">
            <select
              className="w-72 py-0.5 relative inline-flex flex-col justify-start items-end gap-0.5 overflow-hidden border-b border-zinc-300 bg-transparent text-zinc-700 text-base font-normal focus:outline-none pr-8 appearance-none"
              value={mode}
              onChange={(e) =>
                handleModeChange(
                  e.target.value as "weekdays" | "247" | "custom"
                )
              }
              aria-label="Select business hours mode"
              disabled={isReadOnly}
            >
              <option value="weekdays">Weekdays - 8:00 AM to 5:00 PM</option>
              <option value="247">24/7</option>
              <option value="custom">Custom</option>
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-7">
        <div className="flex flex-col justify-start items-start gap-3">
          {Object.entries(workingHours).map(([day, schedule]) => (
            <div key={day} className="flex items-center gap-16">
              <div className="size-6 flex justify-center items-center overflow-hidden">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={!!schedule}
                    onChange={(e) => handleDayToggle(day, e.target.checked)}
                    disabled={mode !== "custom" || isReadOnly}
                    aria-label={`Toggle ${
                      DAY_LABELS[day] || day
                    } working hours`}
                    className={`size-5 p-[3.23px] rounded-xs shadow-[inset_0.8086643218994141px_0.8086643218994141px_1.4151625633239746px_0px_rgba(0,0,0,0.00)] outline outline-[0.61px] flex justify-center items-center overflow-hidden appearance-none ${
                      schedule
                        ? "outline-[#73DCB0]"
                        : "bg-white outline-neutral-200"
                    }`}
                    style={schedule ? { backgroundColor: "#73DCB0" } : {}}
                  />
                  {schedule && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={`w-20 text-sm font-normal leading-tight ${
                  schedule ? "text-zinc-700" : "text-zinc-700"
                }`}
              >
                {DAY_LABELS[day] || day}
              </div>
              {schedule ? (
                <>
                  <div className="w-40 h-7 relative border-b border-zinc-300 overflow-hidden flex items-center">
                    <input
                      type="time"
                      value={minutesToTimeString(schedule.start)}
                      onChange={(e) =>
                        handleTimeChange(day, "start", e.target.value)
                      }
                      className="flex-1 text-right text-zinc-700 text-base font-normal bg-transparent border-none outline-none pr-2"
                      disabled={mode !== "custom" || isReadOnly}
                      aria-label={`Start time for ${DAY_LABELS[day] || day}`}
                    />
                  </div>
                  <div className="text-right text-gray-500 text-sm font-normal leading-tight">
                    To
                  </div>
                  <div className="w-40 h-7 relative border-b border-zinc-300 overflow-hidden flex items-center">
                    <input
                      type="time"
                      value={minutesToTimeString(schedule.end)}
                      onChange={(e) =>
                        handleTimeChange(day, "end", e.target.value)
                      }
                      className="flex-1 text-right text-zinc-700 text-base font-normal bg-transparent border-none outline-none pr-2"
                      disabled={mode !== "custom" || isReadOnly}
                      aria-label={`End time for ${DAY_LABELS[day] || day}`}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="w-40 h-7 relative border-b border-zinc-300 overflow-hidden flex items-center">
                    <div className="flex-1 text-right text-gray-400 text-base font-normal pr-2">
                      --
                    </div>
                  </div>
                  <div className="text-right text-gray-400 text-sm font-normal leading-tight">
                    To
                  </div>
                  <div className="w-40 h-7 relative border-b border-zinc-300 overflow-hidden flex items-center">
                    <div className="flex-1 text-right text-gray-400 text-base font-normal pr-2">
                      --
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div
          className="bg-gray-100 px-2 py-1 rounded-lg text-right text-emerald-500 text-sm font-normal leading-tight cursor-pointer hover:text-emerald-600"
          onClick={handleApplyToAll}
        >
          Apply to all
        </div>
      </div>
    </div>
  );
};
